import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';

/**
 * Helper para agregar la fuente del juego y resolución alta a un estilo de texto.
 * La resolución alta hace que los textos se vean nítidos incluso con pixelArt: true.
 *
 * @param fontFamily Familia a usar. La consola va en Monogram (GAME_FONT_FAMILY), la
 *   pixel font del juego que NO se usa para los nombres de personajes (esos van en
 *   Pixeloid). Monogram tiene un solo peso: NO aplicar `fontStyle: 'bold'` (el faux-bold
 *   del browser arruina la pixel font). La sombra 1px estilo Argentum (sin blur) alcanza
 *   para dar cuerpo y contraste.
 */
function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(
  fontFamily: string,
  style?: T
): T {
  return {
    fontFamily,
    resolution: 2, // Resolución alta para textos nítidos
    shadow: {
      offsetX: 1,
      offsetY: 1,
      color: '#000000',
      blur: 0,
      fill: true,
      stroke: false,
    },
    ...style,
  } as T;
}

type SystemConsoleMessage = { main: Phaser.GameObjects.Text; name?: Phaser.GameObjects.Text };

type SystemConsoleOptions = {
  scene: Phaser.Scene;
  initialX: number;
  initialY: number;
  initialWidth: number;
  initialHeight: number;
  minWidth?: number;
  minHeight?: number;
  depth?: number;
  /** Familia de fuente para el texto (misma que los nombres de entidades). */
  fontFamily?: string;
  chatInputText: Phaser.GameObjects.Text | null;
  chatCursor: Phaser.GameObjects.Text | null;
  getChatIsFocused: () => boolean;
  onPositionSizeChange?: (x: number, y: number, width: number, height: number) => void;
};

export class SystemConsole {
  private readonly scene: Phaser.Scene;
  private readonly depth: number;
  private readonly fontFamily: string;
  private readonly minWidth: number;
  private readonly minHeight: number;
  private readonly getChatIsFocused: () => boolean;
  private readonly onPositionSizeChange?: (x: number, y: number, width: number, height: number) => void;

  private bg: Phaser.GameObjects.Rectangle | null = null;
  private resizeHandle: Phaser.GameObjects.Rectangle | null = null;
  private messages: SystemConsoleMessage[] = [];

  private x: number;
  private y: number;
  private width: number;
  private height: number;

  private isDragging = false;
  private isResizing = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private chatInputText: Phaser.GameObjects.Text | null;
  private chatCursor: Phaser.GameObjects.Text | null;

  constructor(options: SystemConsoleOptions) {
    this.scene = options.scene;
    this.depth = options.depth ?? 810;
    this.fontFamily = options.fontFamily ?? GAME_FONT_FAMILY;
    this.minWidth = options.minWidth ?? 260;
    this.minHeight = options.minHeight ?? 60;
    this.x = options.initialX;
    this.y = options.initialY;
    this.width = options.initialWidth;
    this.height = options.initialHeight;
    this.chatInputText = options.chatInputText;
    this.chatCursor = options.chatCursor;
    this.getChatIsFocused = options.getChatIsFocused;
    this.onPositionSizeChange = options.onPositionSizeChange;

    this.buildUI();
    this.layout();
  }

  isDraggingOrResizing(): boolean {
    return this.isDragging || this.isResizing;
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDraggingOrResizing()) return;

    const { width: screenW, height: screenH } = this.scene.scale;

    if (this.isDragging) {
      // Redondeamos a pixel entero: si el panel queda en una coordenada fraccionaria,
      // cada línea de texto (pixel font) cae en un sub-pixel distinto y se ve nítida
      // en unas líneas y borrosa en otras. Snap a entero mantiene la tipografía pareja.
      const rawX = Math.round(pointer.x - this.dragOffsetX);
      const rawY = Math.round(pointer.y - this.dragOffsetY);

      this.x = Phaser.Math.Clamp(rawX, 0, Math.max(0, screenW - this.width));
      this.y = Phaser.Math.Clamp(rawY, 0, Math.max(0, screenH - this.height));
      this.layout();
      this.notifyChange();
    } else if (this.isResizing) {
      const maxWidth = Math.max(this.minWidth, screenW - this.x);
      const maxHeight = Math.max(this.minHeight, screenH - this.y);

      const newWidth = Phaser.Math.Clamp(pointer.x - this.x, this.minWidth, maxWidth);
      const newHeight = Phaser.Math.Clamp(pointer.y - this.y, this.minHeight, maxHeight);

      this.width = newWidth;
      this.height = newHeight;
      this.layout();
      this.notifyChange();
    }
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging || this.isResizing) {
      this.isDragging = false;
      this.isResizing = false;
      pointer.event.stopPropagation();
    }
  }

  addSystemMessage(text: string, color?: string): void {
    const maxMessages = 200;
    const msg = this.scene.add
      .text(14, 14, text, withGameFont(this.fontFamily, {
        fontSize: '18px',
        color: color ?? '#ffffaa',
        wordWrap: { width: this.width - 8 },
      }))
      .setOrigin(0, 0)
      .setDepth(this.depth + 1)
      .setScrollFactor(0);

    this.messages.push({ main: msg });
    this.trimMessages(maxMessages);
    this.layout();
  }

  addChatMessage(playerName: string, message: string): void {
    // Límite alto para evitar problemas de memoria, pero permitir muchos mensajes
    // El layout() se encargará de mostrar solo los que quepan visualmente
    const maxMessages = 200;

    const nameText = this.scene.add
      .text(14, 14, `${playerName}:`, withGameFont(this.fontFamily, {
        fontSize: '18px',
        color: '#ffffff',
      }))
      .setOrigin(0, 0)
      .setDepth(this.depth + 1)
      .setScrollFactor(0);

    const msg = this.scene.add
      .text(14, 14, ` ${message}`, withGameFont(this.fontFamily, {
        fontSize: '18px',
        color: '#ffffff',
        wordWrap: { width: this.width - 8 },
      }))
      .setOrigin(0, 0)
      .setDepth(this.depth + 1)
      .setScrollFactor(0);

    this.messages.push({ main: msg, name: nameText });
    this.trimMessages(maxMessages);
    this.layout();
  }

  layout(): void {
    if (!this.bg) return;

    const { width: screenW, height: screenH } = this.scene.scale;

    this.width = Phaser.Math.Clamp(this.width, this.minWidth, Math.max(this.minWidth, screenW - 20));
    this.height = Phaser.Math.Clamp(this.height, this.minHeight, Math.max(this.minHeight, screenH - 20));
    this.x = Phaser.Math.Clamp(this.x, 0, Math.max(0, screenW - this.width));
    this.y = Phaser.Math.Clamp(this.y, 0, Math.max(0, screenH - this.height));

    this.bg.setPosition(this.x, this.y);
    this.bg.width = this.width;
    this.bg.height = this.height;

    if (this.resizeHandle) {
      this.resizeHandle.setPosition(this.x + this.width - 8, this.y + this.height - 8);
    }

    const paddingX = 4;
    const paddingY = 5;
    const inputSpacing = 6;
    // Reservar espacio para el input: usar la altura del texto cuando tiene contenido o una altura mínima
    // para asegurar que siempre hay espacio suficiente
    const inputHeight = this.chatInputText ? Math.max(this.chatInputText.height, 14) : 0;
    const reservedForInput = this.chatInputText ? inputHeight + inputSpacing * 2 : 0;

    if (this.messages.length > 0) {
      // Asegurar que hay suficiente espacio entre los mensajes y el input
      const maxY = this.y + this.height - reservedForInput;

      this.messages.forEach((entry) => {
        entry.main.setVisible(false);
        if (entry.name) entry.name.setVisible(false);
      });

      // Calcular dinámicamente cuántos mensajes caben y mostrarlos
      // Primero, determinar qué mensajes caben (desde el más reciente hacia atrás)
      const baseX = this.x + paddingX;
      // Calcular la altura disponible: desde paddingY hasta maxY, con un pequeño margen de seguridad
      const availableHeight = Math.max(0, maxY - (this.y + paddingY) - 2);
      const indicesToShow: number[] = [];
      let totalHeight = 0;
      
      // Calcular qué mensajes caben iterando desde el más reciente
      for (let i = this.messages.length - 1; i >= 0; i--) {
        const entry = this.messages[i];
        const msg = entry.main;
        const name = entry.name;
        
        // Calcular altura temporalmente
        if (name) {
          name.setPosition(baseX, 0);
          const availableWidth = this.width - paddingX * 2 - name.width - 4;
          msg.setStyle({ wordWrap: { width: Math.max(10, availableWidth) } });
        } else {
          msg.setStyle({ wordWrap: { width: this.width - paddingX * 2 } });
        }
        
        const originalText = msg.text;
        if (originalText) {
          msg.setText(originalText);
        }
        
        const lineHeight = Math.max(msg.height, name ? name.height : 0);
        const messageHeight = lineHeight + 4;
        
        if (totalHeight + messageHeight <= availableHeight) {
          indicesToShow.unshift(i); // Mantener orden cronológico
          totalHeight += messageHeight;
        } else {
          break;
        }
      }
      
      // Mostrar los mensajes en orden cronológico
      let offsetY = this.y + paddingY;
      for (const i of indicesToShow) {
        const entry = this.messages[i];
        const msg = entry.main;
        const name = entry.name;

        // Actualizar wordWrap y posicionar primero. Redondeamos a pixel entero para que
        // la pixel font (Text/TTF rasterizado) no quede muestreada en sub-pixel.
        const rowY = Math.round(offsetY);
        if (name) {
          name.setPosition(Math.round(baseX), rowY);
          const availableWidth = this.width - paddingX * 2 - name.width - 4;
          msg.setStyle({ wordWrap: { width: Math.max(10, availableWidth) } });
          msg.setPosition(Math.round(baseX + name.width + 4), rowY);
        } else {
          msg.setStyle({ wordWrap: { width: this.width - paddingX * 2 } });
          msg.setPosition(Math.round(baseX), rowY);
        }
        
        // Forzar recálculo del wordWrap
        const originalText = msg.text;
        if (originalText) {
          msg.setText(originalText);
        }
        
        // Calcular la altura después de actualizar el wordWrap
        const lineHeight = Math.max(msg.height, name ? name.height : 0);
        
        // Verificar que el mensaje cabe antes de mostrarlo (con un pequeño margen)
        if (offsetY + lineHeight > maxY - 2) {
          // Este mensaje no cabe completamente, dejar de mostrar más
          break;
        }
        
        msg.setVisible(true);
        if (name) name.setVisible(true);

        offsetY += lineHeight + 4;
      }
    }

    if (this.chatInputText) {
      const inputY = this.y + this.height - inputSpacing;
      const inputMaxWidth = this.width - paddingX * 2;
      this.chatInputText.setPosition(this.x + paddingX, inputY);
      this.chatInputText.setOrigin(0, 1);
      
      // Actualizar wordWrap y reenvolver el texto si está enfocado
      this.chatInputText.setStyle({ wordWrap: { width: Math.max(10, inputMaxWidth) } });
      if (this.getChatIsFocused()) {
        // Guardar el texto actual y volver a establecerlo para que se reenvuelva con el nuevo ancho
        const currentText = this.chatInputText.text;
        this.chatInputText.setText(currentText);
      } else {
        this.chatInputText.setText('');
      }

      if (this.chatCursor) {
        if (this.getChatIsFocused()) {
          const textWidth = this.chatInputText.width;
          this.chatCursor.setPosition(this.chatInputText.x + textWidth, this.chatInputText.y);
          this.chatCursor.setOrigin(0, 1);
        } else {
          this.chatCursor.setVisible(false);
        }
      }
    }
  }

  getState(): { x: number; y: number; width: number; height: number } {
    return { x: this.x, y: this.y, width: this.width, height: this.height };
  }

  private buildUI(): void {
    this.bg = this.scene.add
      .rectangle(this.x, this.y, this.width, this.height, 0x000000, 0.5)
      .setOrigin(0, 0)
      .setDepth(this.depth)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: false })
      .setStrokeStyle(0);

    this.bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const headerHeight = 18;
      const resizeZoneSize = 18;
      const px = pointer.x;
      const py = pointer.y;
      const inHeader = py >= this.y && py <= this.y + headerHeight;
      const inResizeZone = px >= this.x + this.width - resizeZoneSize && px <= this.x + this.width && py >= this.y + this.height - resizeZoneSize && py <= this.y + this.height;

      if (inHeader) {
        this.isDragging = true;
        this.isResizing = false;
        this.dragOffsetX = px - this.x;
        this.dragOffsetY = py - this.y;
        pointer.event.stopPropagation();
      } else if (inResizeZone) {
        this.isResizing = true;
        this.isDragging = false;
        pointer.event.stopPropagation();
      }
    });

    this.resizeHandle = this.scene.add
      .rectangle(this.x + this.width - 8, this.y + this.height - 8, 14, 14, 0xffffff, 0.7)
      .setOrigin(0.5, 0.5)
      .setDepth(this.depth + 1)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });

    this.resizeHandle.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isResizing = true;
      this.isDragging = false;
      pointer.event.stopPropagation();
    });
  }

  private trimMessages(max: number): void {
    if (this.messages.length <= max) return;
    const toRemove = this.messages.splice(0, this.messages.length - max);
    toRemove.forEach((entry) => {
      entry.main.destroy();
      entry.name?.destroy();
    });
  }

  private notifyChange(): void {
    this.onPositionSizeChange?.(this.x, this.y, this.width, this.height);
  }
}

